import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Mail, Trash2, Download } from 'lucide-react';

const EmailList = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'emailList'),
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setEmails(data);
        setLoading(false);
      },
      (err) => {
        console.error('Email list error:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this email from the list?')) return;
    setDeleteLoading(id);
    try {
      await deleteDoc(doc(db, 'emailList', id));
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to remove email.');
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatDate = (val) => {
    if (!val) return '—';
    if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (val instanceof Date) return val.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return String(val);
  };

  const handleExport = () => {
    const csv = emails.map((e) => `${e.email},${formatDate(e.createdAt)},${e.source || ''}`).join('\n');
    const header = 'Email,Date,Source\n';
    const blob = new Blob([header + csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driscoll-email-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="card p-8 text-center text-secondary-500">
        Loading email list…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-secondary-600">
          {emails.length} email{emails.length !== 1 ? 's' : ''} on the list
        </p>
        <button
          onClick={handleExport}
          disabled={emails.length === 0}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="card overflow-hidden">
        {emails.length === 0 ? (
          <div className="p-12 text-center text-secondary-500">
            <Mail className="w-12 h-12 mx-auto mb-4 text-secondary-300" />
            <p className="font-medium">No emails yet</p>
            <p className="text-sm mt-1">Signups from the homepage will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {emails.map((item) => (
                  <tr key={item.id} className="hover:bg-secondary-50/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">{item.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">{formatDate(item.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">{item.source || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        disabled={deleteLoading === item.id}
                        className="text-red-600 hover:text-red-700 p-2 rounded hover:bg-red-50 disabled:opacity-50"
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailList;
